import { BackendClient } from './clients/BackendClient';
import { IAuthService, AuthService } from '../modules/auth/AuthService';
import { PostMessageRendererBridge } from '@/modules/rendererBridge';
import { getUnityTargetOrigin, isAllowedOrigin } from '@/utils/postMessageOrigin';
import { UndoStack, editorUndo, scenePorts, worksOffline, type SceneWriteRecorder } from '../modules/undoRedo';
import { watchDeletions } from '../modules/undoRedo/deletions';
import { unavailableReason } from '../modules/serviceMonitor/offlineGate';
import { v1 } from './api/v1/client';
import { NotificationServiceAdapter } from './adapters/NotificationServiceAdapter';
import { ThumbnailGenerationService } from '../modules/thumbnailGenerationService/ThumbnailGenerationService';
import { createServices as createBaseServices } from './adapters/DatabaseAdapter';
import { AssetServiceImpl } from '../modules/databaseService/assets';
import { AssetAdapter } from './adapters/AssetAdapter';
import { ClipServiceImpl } from '../modules/databaseService/clips';
import { ClipAdapter } from './adapters/ClipAdapter';
import { SceneServiceImpl } from '../modules/databaseService/scenes';
import { SceneAdapter } from './adapters/SceneAdapter';
import { ProjectServiceImpl } from '../modules/databaseService/projects';
import { ProjectAdapter } from './adapters/ProjectAdapter';
import { TimelineInitAdapter } from './adapters/TimelineInitAdapter';
import { TimelineInit } from '../modules/timelineInit/TimelineInit';
import { StorageService } from '../modules/databaseService/types';
import { IThumbnailGenerationService } from '../modules/thumbnailGenerationService/types';
import { UnityScreenshotService } from '../modules/unityScreenshot/UnityScreenshotService';
import { IUnityScreenshotService } from '../modules/unityScreenshot/types';
import { ScreenshotStorageAdapter, SceneUpdateAdapter } from './adapters/UnityScreenshotAdapter';
import { StoryDurationService } from '../modules/storyData/StoryDurationService';
import { PublishedStoryDurationService } from '../modules/storyData/PublishedStoryDurationService';
import { LibraryAdapter } from './adapters/LibraryAdapter';
import { LibraryDataProvider } from '../modules/library/types';
import { AudioIconServiceImpl } from '../modules/thumbnailGenerationService/AudioIconService';
import { AudioIconService } from '../modules/thumbnailGenerationService/types';
import { SupabaseStorageAdapter as AudioIconStorageAdapter } from './adapters/AudioIconAdapter';
import { KeyframeServiceImpl } from '../modules/keyframes';
import { KeyframeAdapter } from './adapters/KeyframeAdapter';
import { KeyframeBridgeAdapter } from './adapters/KeyframeBridgeAdapter';
import { MapSnapshot } from '@/modules/mapSnapshotService/MapSnapshotService';
import { CopyPasteService, InMemoryCopyPasteService } from '@/modules/copyPaste';
import { MapSnapshotService } from '@/modules/mapSnapshotService/types';
import { MapSnapshotAdapter } from './adapters/MapSnapshotAdapter';
import { UIImageService } from '@/modules/uiImages/uiImagesService';
import { TimelineContextMenuService } from '@/modules/timelineContextMenu';
import {
  TimelineClipAdapter,
  TimelineAssetAdapter,
  TimelineSceneAdapter
} from './adapters/TimelineContextMenuAdapter';
import { SubscriptionPlanService } from '../modules/subscriptionPlan/SubscriptionPlanService';
import { ISubscriptionPlanService } from '../modules/subscriptionPlan/types';
import { OrganizationAdapter } from './adapters/OrganizationAdapter';
import { OrganizationServiceImpl } from '../modules/databaseService/organizations';
import { TemplateSubmissionsServiceImpl } from '../modules/databaseService/templateSubmissions';
import { BillingService } from '../modules/billing/BillingService';
import { IBillingService } from '../modules/billing/types';
import { AnalyticsService, IAnalyticsService } from '../modules/analytics';
import { FabService } from '../modules/fabService/FabService';
import { FabAdapter } from './adapters/FabAdapter';
import { IFabService } from '../modules/fabService/types';
import { PolyPizzaService } from '../modules/polyPizzaService/PolyPizzaService';
import { PolyPizzaAdapter } from './adapters/PolyPizzaAdapter';
import { IPolyPizzaService } from '../modules/polyPizzaService/types';
import { UIElementService } from '../modules/uiElements/UIElementService';
import { UIElementServiceInterface } from '../modules/uiElements/types';
import { UIElementAdapter } from './adapters/UIElementAdapter';
import { UIElementBridgeAdapter } from './adapters/UIElementBridgeAdapter';
import { AssetSwapService, IAssetSwapService } from '../modules/assetSwap';
import { TimelineTransport } from '../modules/timelineTransport/TimelineTransport';
import { runtimeEnv } from '@/lib/runtimeEnv';
import { setFallbackSink } from '@/modules/compressedModels/fallback';

// Define the services interface
export interface Services {
  storage: StorageService;
  bridge: PostMessageRendererBridge;
  undo: UndoStack;
  sceneWrites: SceneWriteRecorder;
  notification: NotificationServiceAdapter;
  thumbnailGeneration: IThumbnailGenerationService;
  audioIcon: AudioIconService;
  asset: AssetServiceImpl;
  clip: ClipServiceImpl;
  scene: SceneServiceImpl;
  project: ProjectServiceImpl;
  unityScreenshot: IUnityScreenshotService;
  storyDuration: StoryDurationService;
  publishedStoryDuration: PublishedStoryDurationService;
  library: LibraryDataProvider;
  keyframe: KeyframeServiceImpl;
  mapSnapshot: MapSnapshotService;
  copyPaste: CopyPasteService;
  uiImages: UIImageService;
  timelineInit: TimelineInit;
  timelineContextMenu: TimelineContextMenuService;
  subscriptionPlan: ISubscriptionPlanService;
  organization: OrganizationServiceImpl;
  templateSubmissions: TemplateSubmissionsServiceImpl;
  billing: IBillingService;
  supabase: typeof BackendClient;
  auth: IAuthService;
  analytics: IAnalyticsService;
  fab?: IFabService;
  polyPizza?: IPolyPizzaService;
  assetSwap: IAssetSwapService;
  uiElement: UIElementServiceInterface;
  timelineTransport: TimelineTransport;
}

export function createServices(): Services {
  // Create base services (storage via backend)
  const baseServices = createBaseServices();

  // The editor's only line to its renderer, and its session-local undo/redo, shared by every services object
  const { bridge, undo, sceneWrites } = editorUndo(v1, () => new PostMessageRendererBridge({ allows: isAllowedOrigin, target: getUnityTargetOrigin }), scenePorts);
  watchDeletions(undo);
  // Offline, a step that writes more than the scene's own rows waits in the history, however its undo or redo was pressed
  undo.blockWhen(step => !!unavailableReason() && !worksOffline(step.rows));

  // Create timeline transport (typed play/pause/stop/seek messages)
  const timelineTransport = new TimelineTransport(bridge);

  const notificationService = new NotificationServiceAdapter();

  // Analytics service (PostHog bound at runtime via PostHogAnalyticsBinder)
  const analyticsService = new AnalyticsService();
  // A compressed model that gave way to its original (asset_compressed_fallback), to PostHog
  setFallbackSink('posthog', (event, properties) => analyticsService.trackLureoEvent(event, properties));

  // 1. Clip service (no dependencies on other services)
  const clipAdapter = new ClipAdapter(bridge);
  const clipService = new ClipServiceImpl(clipAdapter);

  // 2. Asset service (depends on clip service; analytics for Lureo_Asset_Added)
  const assetAdapter = new AssetAdapter(bridge, analyticsService);
  const assetService = new AssetServiceImpl(assetAdapter, clipService);

  // 3. Scene service (depends on asset action adapter; analytics for Lureo_New_Scene_Created)
  const sceneAdapter = new SceneAdapter(bridge, analyticsService);
  const sceneService = new SceneServiceImpl(sceneAdapter);

  // Inject scene service into asset service for anchor category updates
  (assetService as any).sceneService = sceneService;

  // 4. Project service (depends on scene adapter)
  const projectAdapter = new ProjectAdapter(sceneAdapter, analyticsService);
  const projectService = new ProjectServiceImpl(projectAdapter);

  // Create unity screenshot adapters and service
  const screenshotStorageAdapter = new ScreenshotStorageAdapter(baseServices.storage);
  const sceneUpdateAdapter = new SceneUpdateAdapter(sceneService);
  const unityScreenshotService = new UnityScreenshotService(screenshotStorageAdapter, sceneUpdateAdapter, bridge);

  // Create thumbnail generation service
  const thumbnailGenerationService = new ThumbnailGenerationService({});


  // Map snapshot service
  const mapSnapshotAdapter = new MapSnapshotAdapter();
  const mapSnapshotService = new MapSnapshot(
    mapSnapshotAdapter,
    runtimeEnv.VITE_GOOGLE_API_KEY || process.env.VITE_GOOGLE_API_KEY || ''
  );

  // Create story duration service
  const storyDurationService = new StoryDurationService();

  // Create published story duration service
  const publishedStoryDurationService = new PublishedStoryDurationService();

  // Inject the published story duration service into the scene adapter
  sceneAdapter.setPublishedStoryDurationService(publishedStoryDurationService);

  // Create asset library service
  const libraryService = new LibraryAdapter();

  // Create audio icon service — needs a StorageProvider (with .from())
  const audioIconService = new AudioIconServiceImpl({ storage: new AudioIconStorageAdapter() });

  // Create keyframe service with action provider
  const keyframeAdapter = new KeyframeAdapter();
  const keyframeService = new KeyframeServiceImpl(keyframeAdapter, new KeyframeBridgeAdapter(bridge));

  // Create UI Images service
  const uiImagesStorageAdapter = {
    uploadFile: async (path: string, file: File | Blob, options?: any) => {
      const result = await BackendClient.storage.from('image_files').upload(path, file, options);
      return { data: result.data, error: result.error ? new Error(result.error.message) : undefined };
    },
    getPublicUrl: (path: string) => BackendClient.storage.from('image_files').getPublicUrl(path)
  };

  const uiImagesService = new UIImageService(uiImagesStorageAdapter);

  // Copy/Paste service (simple in-memory clipboard)
  const copyPasteService = new InMemoryCopyPasteService();

  // Create timeline init service
  const timelineInitAdapter = new TimelineInitAdapter();
  const timelineInitService = new TimelineInit(timelineInitAdapter);

  // Create timeline context menu service
  // Note: State and callback providers will be injected at component level
  const timelineClipAdapter = new TimelineClipAdapter(clipService);
  
  // Placeholder adapters - actual implementations will be provided by components
  const placeholderAssetAdapter = new TimelineAssetAdapter(
    () => { throw new Error('Asset adapter not properly injected'); },
    async () => { throw new Error('Asset updater not properly injected'); }
  );
  
  
  const placeholderSceneAdapter = new TimelineSceneAdapter(
    () => { throw new Error('Scene instance getter not properly injected'); },
    () => { throw new Error('Scene ID getter not properly injected'); }
  );

  // Create base timeline context menu service - will be enhanced at component level
  const timelineContextMenuService = new TimelineContextMenuService(
    timelineClipAdapter,
    placeholderAssetAdapter,
    // State and callback providers will be injected at component level
    {} as any, // TimelineStateProvider - injected by component
    {} as any, // TimelineCallbackProvider - injected by component  
    placeholderSceneAdapter
  );

  // Create subscription plan service
  const subscriptionPlanService = new SubscriptionPlanService();

  // Create organization service
  const organizationAdapter = new OrganizationAdapter();
  const organizationService = new OrganizationServiceImpl(organizationAdapter);
  const templateSubmissionsService = new TemplateSubmissionsServiceImpl();

  // Create billing service - uses backend API directly
  const billingService = new BillingService();

  const authService = new AuthService();

  // Create Fab service (Sketchfab)
  // API key is now handled server-side by the Supabase Edge Function proxy
  const fabApiUrl = runtimeEnv.VITE_FAB_API_URL || process.env.VITE_FAB_API_URL;
  const fabAdapter = new FabAdapter(fabApiUrl);
  const fabService = new FabService(fabAdapter);

  // Create Poly Pizza service
  // API key is now handled server-side by the Supabase Edge Function proxy
  const polyPizzaApiUrl = runtimeEnv.VITE_POLY_PIZZA_API_URL || process.env.VITE_POLY_PIZZA_API_URL;
  const polyPizzaAdapter = new PolyPizzaAdapter(polyPizzaApiUrl);
  const polyPizzaService = new PolyPizzaService(polyPizzaAdapter);

  // Create UI element service
  const uiElementAdapter = new UIElementAdapter();
  const uiElementService = new UIElementService(uiElementAdapter, new UIElementBridgeAdapter(bridge));

  // Create services object first (without assetSwap to avoid circular dependency)
  const servicesWithoutAssetSwap: Omit<Services, 'assetSwap'> = {
    ...baseServices,
    supabase: BackendClient,
    auth: authService,
    asset: assetService,
    clip: clipService,
    scene: sceneService,
    project: projectService,
    bridge,
    undo,
    sceneWrites,
    notification: notificationService,
    unityScreenshot: unityScreenshotService,
    thumbnailGeneration: thumbnailGenerationService,
    audioIcon: audioIconService,
    storyDuration: storyDurationService,
    publishedStoryDuration: publishedStoryDurationService,
    library: libraryService,
    keyframe: keyframeService,
    mapSnapshot: mapSnapshotService,
    copyPaste: copyPasteService,
    uiImages: uiImagesService,
    timelineInit: timelineInitService,
    timelineContextMenu: timelineContextMenuService,
    subscriptionPlan: subscriptionPlanService,
    organization: organizationService,
    templateSubmissions: templateSubmissionsService,
    billing: billingService,
    analytics: analyticsService,
    fab: fabService,
    polyPizza: polyPizzaService,
    uiElement: uiElementService,
    timelineTransport: timelineTransport
  };

  // Create asset swap service with the services object
  const assetSwapService = new AssetSwapService(
    assetService,
    keyframeService,
    clipService,
    bridge,
    libraryService,
    { ...servicesWithoutAssetSwap, assetSwap: null as any } as Services
  );

  // Now create the complete services object
  const services: Services = {
    ...servicesWithoutAssetSwap,
    assetSwap: assetSwapService
  };

  // Update assetSwap's services reference to point to the complete services object
  (assetSwapService as any).services = services;

  return services;
}
